import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFirestore, doc, getDoc, setDoc, deleteDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, uploadString, getDownloadURL, deleteObject } from "firebase/storage";
import { normalizePolygonPath } from "../../../helpers/podcastHelpers";
import { parsePolygonGeoJsonFeatures } from "../../../helpers/modelBuilder/mbChatPureUtils";
import SimulationSelector from "../../SectionScreens/SimulationSelector";
import ImageVariablesSection from "./ImageVariablesSection";
import VideoVariablesSection from "./VideoVariablesSection";

const POLYGON_KEY_REGEX = /^[a-zA-Z0-9_-]+$/;
const DEFAULT_PARTNER_LOGO_TIERS = [
  { key: "main_partners", name: "Main partners", rank: 1 },
  { key: "supporting_partners", name: "Supporting partners", rank: 2 },
  { key: "other_partners", name: "Other partners", rank: 3 }
];
const PARTNER_LOGO_TIER_KEYS = new Set(DEFAULT_PARTNER_LOGO_TIERS.map((tier) => tier.key));

const normalizePartnerLogoTierKey = (logoOrTier) => {
  const raw = typeof logoOrTier === "string"
    ? logoOrTier
    : (logoOrTier?.tierKey || logoOrTier?.tier || "");
  if (PARTNER_LOGO_TIER_KEYS.has(raw)) return raw;
  if (raw === "lead") return "main_partners";
  if (raw === "supporting") return "supporting_partners";
  return "supporting_partners";
};

const normalizePartnerLogoTiers = (rawTiers) => {
  const byKey = new Map(
    (Array.isArray(rawTiers) ? rawTiers : [])
      .filter((tier) => PARTNER_LOGO_TIER_KEYS.has(tier?.key))
      .map((tier) => [tier.key, tier])
  );

  return DEFAULT_PARTNER_LOGO_TIERS.map((tier) => {
    const saved = byKey.get(tier.key);
    return {
      ...tier,
      name: String(saved?.name || tier.name).trim() || tier.name
    };
  });
};

const sanitizePolygonKey = (value, fallback = "geojson_polygon") => {
  const cleaned = String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || fallback;
};

const computePolygonCenter = (points = []) => {
  if (!points.length) return { lat: 0, lng: 0 };
  const { latSum, lngSum } = points.reduce(
    (acc, pt) => ({
      latSum: acc.latSum + (Number(pt.lat) || 0),
      lngSum: acc.lngSum + (Number(pt.lng) || 0)
    }),
    { latSum: 0, lngSum: 0 }
  );
  return {
    lat: latSum / points.length,
    lng: lngSum / points.length
  };
};

const useAutosizeTextarea = (ref, value, { min = 10, max = 260 } = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${next}px`;
  }, [ref, value, min, max]);
};

const safeStringify = (value, fallback) => {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch {
    return JSON.stringify(fallback, null, 2);
  }
};

const tryParseJson = (text) => {
  try {
    return { ok: true, value: JSON.parse(text), error: "" };
  } catch {
    return { ok: false, value: null, error: "Invalid JSON. Fix formatting and try again." };
  }
};

const FirestoreJsonEditor = ({
  title,
  docRef,
  isActive,
  defaultValue,
  pickFromDoc,
  buildMergeUpdate,
  minHeight = 10,
  maxHeight = 260
}) => {
  const [text, setText] = useState(safeStringify(defaultValue, defaultValue));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const taRef = useRef(null);

  useAutosizeTextarea(taRef, text, { min: minHeight, max: maxHeight });
  const parsed = useMemo(() => tryParseJson(text), [text]);

  const load = async () => {
    if (!docRef) return;
    try {
      setLoading(true);
      setError("");
      setStatus("");

      const snap = await getDoc(docRef);
      const data = snap.exists() ? (snap.data() || {}) : {};

      const picked = pickFromDoc ? pickFromDoc(data) : data;
      setText(safeStringify(picked, defaultValue));
      setStatus("Loaded.");
    } catch (e) {
      console.error(e);
      setError("Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!docRef) return;

    const p = tryParseJson(text);
    if (!p.ok) {
      setError(p.error);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setStatus("");

      const update = buildMergeUpdate ? buildMergeUpdate(p.value) : p.value;
      if (update == null || typeof update !== "object") {
        setError("JSON must be an object at the top-level.");
        return;
      }

      await setDoc(docRef, update, { merge: true });
      setStatus("Saved.");
    } catch (e) {
      console.error(e);
      setError("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    load();
  }, [isActive, docRef?.path]);

  if (!isActive) return null;

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontWeight: 800 }}>{title}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} disabled={loading || !docRef}>
            {loading ? "Loading…" : "Reload"}
          </button>
          <button onClick={save} disabled={saving || !docRef || !parsed.ok}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight,
            maxHeight,
            overflowY: "auto",
            resize: "none",
            padding: 10,
            borderRadius: 8,
            border: "1px solid #ddd",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre",
            lineHeight: 1.35
          }}
        />
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
        {error && <div style={{ color: "crimson", fontWeight: 700 }}>{error}</div>}
        {!error && !parsed.ok && <div style={{ color: "crimson", fontWeight: 700 }}>{parsed.error}</div>}
        {!error && parsed.ok && status && <div style={{ color: "#065f46", fontWeight: 700 }}>{status}</div>}
      </div>
    </div>
  );
};

const GeneralDetailsTab = ({
  isActive,
  podcastId,
  selectedLanguage,
  polygonDraftPoints = [],
  isSelectingPolygon = false,
  onStartPolygon,
  onCancelPolygon,
  onUndoPolygonPoint,
  onPolygonSaved,
  onPreviewPolygon,
  onClearPreviewPolygon,
  currentPreviewPolygonKey = "",
  defaultLocation = null,
  defaultLocationDraft = null,
  defaultZoom = { desktop: 14.5, mobile: 14.5 },
  defaultZoomDraft = { desktop: 14.5, mobile: 14.5 },
  onDefaultZoomDraftChange,
  onSaveDefaultLocation,
  isSavingDefaultLocation = false,
  defaultLocationStatus = "",
  defaultLocationError = "",
  defaultLocationLoading = false,
  isPickingDefaultLocation = false,
  onStartDefaultLocationPick,
  onCancelDefaultLocationPick,
  simulations = [],
  defaultSimulationId = "",
  defaultSimulationDraft = "",
  onDefaultSimulationSelect,
  isSavingDefaultSimulation = false,
  defaultSimulationStatus = "",
  defaultSimulationError = "",
  oneCarSimulationId = "",
  oneCarSimulationDraft = "",
  onOneCarSimulationSelect,
  isSavingOneCarSimulation = false,
  oneCarSimulationStatus = "",
  oneCarSimulationError = "",
  onImageVariablesChange
}) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const langCode = String(selectedLanguage || "EN").toUpperCase();

  const podcastDocRef = useMemo(() => {
    if (!podcastId) return null;
    return doc(db, "podcasts", String(podcastId));
  }, [db, podcastId]);

  const generalDetailsRef = useMemo(() => {
    if (!podcastId || !langCode) return null;
    return doc(db, "podcasts", String(podcastId), "general_details", langCode);
  }, [db, podcastId, langCode]);

  const [polygonName, setPolygonName] = useState("");
  const [polygonSaving, setPolygonSaving] = useState(false);
  const [polygonStatus, setPolygonStatus] = useState("");
  const [polygonError, setPolygonError] = useState("");
  const [polygons, setPolygons] = useState([]);
  const [polygonsLoading, setPolygonsLoading] = useState(false);
  const [polygonsError, setPolygonsError] = useState("");
  const [geoJsonUploading, setGeoJsonUploading] = useState(false);
  const [geoJsonKeyPrefix, setGeoJsonKeyPrefix] = useState("");
  const [previewLoadingKey, setPreviewLoadingKey] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [activeNodes, setActiveNodes] = useState([]);
  const [activeNodesInput, setActiveNodesInput] = useState("");
  const [activeNodesStatus, setActiveNodesStatus] = useState("");
  const [activeNodesError, setActiveNodesError] = useState("");
  const activeNodesLoadingRef = useRef(false);
  const [podcastTitle, setPodcastTitle] = useState("");
  const [podcastCta, setPodcastCta] = useState("");
  const [podcastCopyStatus, setPodcastCopyStatus] = useState("");
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [partnerLogosLoading, setPartnerLogosLoading] = useState(false);
  const [partnerLogoUploading, setPartnerLogoUploading] = useState(false);
  const [partnerLogoStatus, setPartnerLogoStatus] = useState("");
  const [partnerLogoError, setPartnerLogoError] = useState("");
  const [partnerLogoTier, setPartnerLogoTier] = useState("supporting_partners");
  const [partnerLogoTiers, setPartnerLogoTiers] = useState(DEFAULT_PARTNER_LOGO_TIERS);
  const [locationMode, setLocationMode] = useState("multiple");
  const [locationModeLoading, setLocationModeLoading] = useState(true);
  const [locationModeSaving, setLocationModeSaving] = useState(false);
  const [locationModeError, setLocationModeError] = useState("");

  useEffect(() => {
    if (!podcastDocRef) {
      setLocationMode("multiple");
      setLocationModeLoading(false);
      return;
    }

    let cancelled = false;
    setLocationModeLoading(true);
    getDoc(podcastDocRef)
      .then((snap) => {
        if (cancelled) return;
        const savedMode = snap.exists() ? snap.data()?.location_mode : "";
        setLocationMode(savedMode === "single" ? "single" : "multiple");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load location mode:", err);
        setLocationModeError("Failed to load location mode.");
      })
      .finally(() => {
        if (!cancelled) setLocationModeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [podcastDocRef]);

  const saveLocationMode = async (nextMode) => {
    if (!podcastDocRef || locationModeSaving || nextMode === locationMode) return;
    const previousMode = locationMode;
    setLocationMode(nextMode);
    setLocationModeSaving(true);
    setLocationModeError("");
    try {
      await setDoc(
        podcastDocRef,
        { location_mode: nextMode, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to save location mode:", err);
      setLocationMode(previousMode);
      setLocationModeError("Failed to save location mode.");
    } finally {
      setLocationModeSaving(false);
    }
  };

  const formatZoom = (zoom) => {
    const desktop = Number(zoom?.desktop);
    const mobile = Number(zoom?.mobile);
    const desktopLabel = Number.isFinite(desktop) ? desktop : "—";
    const mobileLabel = Number.isFinite(mobile) ? mobile : "—";
    return `Desktop ${desktopLabel}, Mobile ${mobileLabel}`;
  };

  const updateZoomField = (key, value) => {
    if (value === "") {
      onDefaultZoomDraftChange?.({
        ...(defaultZoomDraft || {}),
        [key]: ""
      });
      return;
    }

    const parsed = Number(value);
    onDefaultZoomDraftChange?.({
      ...(defaultZoomDraft || {}),
      [key]: Number.isFinite(parsed) ? parsed : defaultZoomDraft?.[key]
    });
  };

  const loadPartnerLogos = useCallback(async () => {
    if (!podcastDocRef) return;
    try {
      setPartnerLogosLoading(true);
      setPartnerLogoError("");
      const snap = await getDoc(podcastDocRef);
      const raw = snap.exists() ? snap.data()?.partner_logos : [];
      const tiers = normalizePartnerLogoTiers(snap.exists() ? snap.data()?.partner_logo_tiers : []);
      setPartnerLogoTiers(tiers);
      const logos = Array.isArray(raw) ? raw : [];
      const resolved = await Promise.all(logos.map(async (logo) => {
        const storagePath = String(logo?.storagePath || "");
        const url = storagePath
          ? await getDownloadURL(storageRef(storage, storagePath)).catch(() => logo?.url || "")
          : (logo?.url || "");
        const tierKey = normalizePartnerLogoTierKey(logo);
        return { ...logo, tierKey, url };
      }));
      setPartnerLogos(resolved);
    } catch (err) {
      console.error("Failed to load partner logos:", err);
      setPartnerLogoError("Failed to load partner logos.");
    } finally {
      setPartnerLogosLoading(false);
    }
  }, [podcastDocRef, storage]);

  useEffect(() => {
    if (!isActive || !podcastDocRef) return;
    loadPartnerLogos();
  }, [isActive, podcastDocRef, loadPartnerLogos]);

  const uploadPartnerLogo = async (file) => {
    if (!file || !podcastDocRef || !podcastId) return;
    try {
      setPartnerLogoUploading(true);
      setPartnerLogoStatus("");
      setPartnerLogoError("");

      const safeName = String(file.name || "logo")
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const storagePath = `Podcasts/${String(podcastId)}/partner-logos/${Date.now()}-${safeName || "logo"}`;
      await uploadBytes(storageRef(storage, storagePath), file, { contentType: file.type || "image/png" });
      const url = await getDownloadURL(storageRef(storage, storagePath)).catch(() => "");

      const snap = await getDoc(podcastDocRef);
      const existing = snap.exists() && Array.isArray(snap.data()?.partner_logos)
        ? snap.data().partner_logos
        : [];
      const next = [
        ...existing,
        {
          name: safeName || file.name || "Partner logo",
          tierKey: normalizePartnerLogoTierKey(partnerLogoTier),
          storagePath,
          url,
          uploadedAt: new Date().toISOString()
        }
      ];

      await setDoc(podcastDocRef, { partner_logos: next, updatedAt: serverTimestamp() }, { merge: true });
      setPartnerLogos(next);
      setPartnerLogoStatus("Partner logo uploaded.");
    } catch (err) {
      console.error("Failed to upload partner logo:", err);
      setPartnerLogoError("Failed to upload partner logo.");
    } finally {
      setPartnerLogoUploading(false);
    }
  };

  const removePartnerLogo = async (idx) => {
    if (!podcastDocRef) return;
    try {
      setPartnerLogoStatus("");
      setPartnerLogoError("");
      const next = partnerLogos.filter((_, i) => i !== idx);
      await setDoc(podcastDocRef, { partner_logos: next, updatedAt: serverTimestamp() }, { merge: true });
      setPartnerLogos(next);
      setPartnerLogoStatus("Partner logo removed.");
    } catch (err) {
      console.error("Failed to remove partner logo:", err);
      setPartnerLogoError("Failed to remove partner logo.");
    }
  };

  const updatePartnerLogoTier = async (idx, tier) => {
    if (!podcastDocRef) return;
    const normalizedTier = normalizePartnerLogoTierKey(tier);
    try {
      setPartnerLogoStatus("");
      setPartnerLogoError("");
      const next = partnerLogos.map((logo, i) =>
        i === idx ? { ...logo, tierKey: normalizedTier } : logo
      );
      await setDoc(podcastDocRef, { partner_logos: next, updatedAt: serverTimestamp() }, { merge: true });
      setPartnerLogos(next);
      setPartnerLogoStatus("Partner logo tier updated.");
    } catch (err) {
      console.error("Failed to update partner logo tier:", err);
      setPartnerLogoError("Failed to update partner logo tier.");
    }
  };

  const updatePartnerLogoTierName = async (key, name) => {
    if (!podcastDocRef || !PARTNER_LOGO_TIER_KEYS.has(key)) return;
    const next = partnerLogoTiers.map((tier) =>
      tier.key === key ? { ...tier, name } : tier
    );
    setPartnerLogoTiers(next);

    try {
      setPartnerLogoStatus("");
      setPartnerLogoError("");
      await setDoc(
        podcastDocRef,
        {
          partner_logo_tiers: next.map((tier) => ({
            key: tier.key,
            name: String(tier.name || "").trim() || DEFAULT_PARTNER_LOGO_TIERS.find((t) => t.key === tier.key)?.name || tier.key,
            rank: tier.rank
          })),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
      setPartnerLogoStatus("Partner tier name updated.");
    } catch (err) {
      console.error("Failed to update partner tier name:", err);
      setPartnerLogoError("Failed to update partner tier name.");
    }
  };

  const loadPolygons = useCallback(async () => {
    if (!podcastId) return;
    try {
      setPolygonsLoading(true);
      setPolygonsError("");

      const snap = await getDocs(collection(db, "podcasts", String(podcastId), "polygons"));
      const items = snap.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          const displayName = data.displayName || docSnap.id;
          return {
            key: docSnap.id,
            displayName,
            pointCount: Number(data.pointCount) || 0,
            center: data.center || null,
            updatedAt: data.updatedAt,
            storagePath: data.storagePath || ""
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      setPolygons(items);
    } catch (err) {
      console.error("Failed to load polygons", err);
      setPolygonsError("Failed to load polygons.");
    } finally {
      setPolygonsLoading(false);
    }
  }, [db, podcastId]);

  useEffect(() => {
    if (!isActive || !podcastId) return;
    loadPolygons();
  }, [isActive, podcastId, loadPolygons]);

  useEffect(() => {
    setPolygonName("");
    setPolygonError("");
    setPolygonStatus("");
    setPreviewError("");
    setPreviewLoadingKey("");
    onClearPreviewPolygon?.();
  }, [podcastId, onClearPreviewPolygon]);

  const fetchPolygonPath = useCallback(
    async (poly) => {
      if (!storage || !podcastId) return [];
      const storagePath = poly.storagePath || `Podcasts/${podcastId}/polygons/${poly.key}.json`;
      const url = await getDownloadURL(storageRef(storage, storagePath));
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Failed to fetch polygon JSON (${resp.status})`);
      const json = await resp.json();
      return normalizePolygonPath(json);
    },
    [podcastId, storage]
  );

  const handleSavePolygon = async () => {
    if (!podcastId) {
      setPolygonError("Select a podcast before saving.");
      return;
    }
    const trimmed = polygonName.trim();
    if (!trimmed) {
      setPolygonError("Enter a polygon key.");
      return;
    }
    if (!POLYGON_KEY_REGEX.test(trimmed)) {
      setPolygonError("Use letters, numbers, underscores or hyphens.");
      return;
    }

    const points = (polygonDraftPoints || [])
      .map((pt) => ({
        lat: Number(pt?.lat),
        lng: Number(pt?.lng)
      }))
      .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));

    if (points.length < 3) {
      setPolygonError("Click at least three points on the map before saving.");
      return;
    }

    try {
      setPolygonSaving(true);
      setPolygonError("");
      setPolygonStatus("");

      const storagePath = `Podcasts/${podcastId}/polygons/${trimmed}.json`;
      const payload = JSON.stringify(points, null, 2);

      await uploadString(
        storageRef(storage, storagePath),
        payload,
        "raw",
        { contentType: "application/json" }
      );

      const center = computePolygonCenter(points);

      await setDoc(
        doc(db, "podcasts", String(podcastId), "polygons", trimmed),
        {
          name: trimmed,
          displayName: trimmed,
          center,
          pointCount: points.length,
          storagePath,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      setPolygonStatus("Polygon saved.");
      setPolygonName("");
      onPolygonSaved?.();
      await loadPolygons();
    } catch (err) {
      console.error("Failed to save polygon", err);
      setPolygonError("Failed to save polygon. Try again.");
    } finally {
      setPolygonSaving(false);
    }
  };

  const savePolygonPath = async ({
    key,
    displayName,
    points,
    source = "manual",
    geometryType = ""
  }) => {
    const storagePath = `Podcasts/${podcastId}/polygons/${key}.json`;
    const payload = JSON.stringify(points, null, 2);

    await uploadString(
      storageRef(storage, storagePath),
      payload,
      "raw",
      { contentType: "application/json" }
    );

    const center = computePolygonCenter(points);

    await setDoc(
      doc(db, "podcasts", String(podcastId), "polygons", key),
      {
        name: key,
        displayName: displayName || key,
        center,
        pointCount: points.length,
        storagePath,
        source,
        geometryType,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  };

  const handleGeoJsonUpload = async (file) => {
    if (!file) return;
    if (!podcastId) {
      setPolygonError("Select a podcast before uploading GeoJSON.");
      return;
    }

    try {
      setGeoJsonUploading(true);
      setPolygonError("");
      setPolygonStatus("");

      const rawText = await file.text();
      const parsedPolygons = parsePolygonGeoJsonFeatures(rawText);
      if (!parsedPolygons.length) {
        setPolygonError("No Polygon or MultiPolygon features were found in that GeoJSON.");
        return;
      }

      const usedKeys = new Set();
      let savedCount = 0;
      const customPrefix = sanitizePolygonKey(geoJsonKeyPrefix, "");
      const useCustomPrefix = Boolean(customPrefix);
      const totalPolygons = parsedPolygons.length;

      for (const item of parsedPolygons) {
        const outerRing = Array.isArray(item?.paths?.[0]) ? item.paths[0] : [];
        const points = outerRing
          .map((pt) => ({
            lat: Number(pt?.lat),
            lng: Number(pt?.lng)
          }))
          .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));

        if (points.length < 3) continue;

        const baseKey = useCustomPrefix
          ? (totalPolygons === 1 ? customPrefix : `${customPrefix}_${savedCount + 1}`)
          : sanitizePolygonKey(item?.polygon_id, `geojson_polygon_${savedCount + 1}`);
        let key = baseKey;
        let suffix = 2;
        while (usedKeys.has(key)) {
          key = `${baseKey}_${suffix}`;
          suffix += 1;
        }
        usedKeys.add(key);

        await savePolygonPath({
          key,
          displayName: item?.feature_name || key,
          points,
          source: "geojson",
          geometryType: item?.geometry_type || ""
        });
        savedCount += 1;
      }

      if (!savedCount) {
        setPolygonError("GeoJSON was parsed, but no valid outer polygon rings were found.");
        return;
      }

      setPolygonStatus(`Uploaded ${savedCount} polygon${savedCount === 1 ? "" : "s"} from GeoJSON.`);
      onPolygonSaved?.();
      await loadPolygons();
    } catch (err) {
      console.error("Failed to upload GeoJSON polygons", err);
      setPolygonError("Failed to upload GeoJSON polygons.");
    } finally {
      setGeoJsonUploading(false);
    }
  };

  const polygonPointsCount = Array.isArray(polygonDraftPoints) ? polygonDraftPoints.length : 0;
  const canSavePolygon = polygonPointsCount >= 3 && polygonName.trim().length > 0;
  const formatCenter = (center) => {
    if (!center) return null;
    const lat = Number(center.lat ?? center.latitude);
    const lng = Number(center.lng ?? center.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  };
  const handleTogglePolygonSelection = () => {
    setPolygonStatus("");
    setPolygonError("");
    if (isSelectingPolygon) {
      onCancelPolygon?.();
    } else {
      onStartPolygon?.();
    }
  };

  const handleRefreshPolygons = () => {
    loadPolygons();
  };

  const handlePreviewPolygonClick = async (poly) => {
    if (!poly?.key) return;
    if (currentPreviewPolygonKey === poly.key) {
      setPreviewError("");
      setPreviewLoadingKey("");
      onClearPreviewPolygon?.();
      return;
    }

    try {
      setPreviewError("");
      setPreviewLoadingKey(poly.key);
      const path = await fetchPolygonPath(poly);
      if (!path.length) {
        throw new Error("Polygon has no coordinates.");
      }
      onPreviewPolygon?.({
        key: poly.key,
        displayName: poly.displayName,
        path
      });
    } catch (err) {
      console.error("Failed to load polygon for preview:", err);
      setPreviewError("Failed to load polygon for preview.");
    } finally {
      setPreviewLoadingKey("");
    }
  };

  const handleDeletePolygon = async (poly) => {
    if (!poly?.key || !podcastId) return;
    const ok = window.confirm(`Delete polygon "${poly.displayName || poly.key}"?`);
    if (!ok) return;

    try {
      setPolygonStatus("");
      setPolygonError("");
      setPreviewError("");

      const storagePath = poly.storagePath || `Podcasts/${podcastId}/polygons/${poly.key}.json`;
      await deleteObject(storageRef(storage, storagePath)).catch((err) => {
        console.warn("Failed to delete polygon storage object:", err);
      });
      await deleteDoc(doc(db, "podcasts", String(podcastId), "polygons", poly.key));

      if (currentPreviewPolygonKey === poly.key) {
        onClearPreviewPolygon?.();
      }

      setPolygonStatus("Polygon deleted.");
      await loadPolygons();
    } catch (err) {
      console.error("Failed to delete polygon:", err);
      setPolygonError("Failed to delete polygon.");
    }
  };

  const simulationNameForId = useCallback(
    (id) => {
      if (!id) return "";
      const match = (simulations || []).find((s) => String(s.id) === String(id));
      return match?.name || id;
    },
    [simulations]
  );


  useEffect(() => {
    const loadActiveNodes = async () => {
      if (!generalDetailsRef || activeNodesLoadingRef.current) return;
      activeNodesLoadingRef.current = true;
      try {
        const snap = await getDoc(generalDetailsRef);
        const data = snap.exists() ? snap.data() : {};
        const fromDoc = Array.isArray(data.active_nodes) ? data.active_nodes : [];
        setActiveNodes(fromDoc);
        setPodcastTitle(String(data.title || ""));
        setPodcastCta(String(data.cta_label || ""));
        setActiveNodesStatus("");
        setActiveNodesError("");
      } catch (err) {
        console.error("Failed to load active nodes:", err);
        setActiveNodesError("Failed to load active nodes.");
      } finally {
        activeNodesLoadingRef.current = false;
      }
    };
    loadActiveNodes();
  }, [generalDetailsRef]);

  const savePodcastCopy = async () => {
    if (!generalDetailsRef) return;
    setPodcastCopyStatus("Saving...");
    try {
      await setDoc(generalDetailsRef, {
        title: podcastTitle.trim(),
        cta_label: podcastCta.trim()
      }, { merge: true });
      setPodcastCopyStatus("Saved.");
    } catch (err) {
      console.error("Failed to save podcast landing copy:", err);
      setPodcastCopyStatus("Failed to save.");
    }
  };

  const handleAddActiveNodes = async () => {
    const raw = activeNodesInput.trim();
    if (!raw) {
      setActiveNodesError("Enter node IDs separated by commas.");
      return;
    }
    const parsed = raw
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    if (!parsed.length) {
      setActiveNodesError("Enter at least one node id.");
      return;
    }
    const merged = Array.from(new Set([...activeNodes, ...parsed]));
    try {
      setActiveNodesStatus("Saving...");
      setActiveNodesError("");
      await setDoc(
        generalDetailsRef,
        { active_nodes: merged },
        { merge: true }
      );
      setActiveNodes(merged);
      setActiveNodesInput("");
      setActiveNodesStatus("Saved.");
    } catch (err) {
      console.error("Failed to save active nodes:", err);
      setActiveNodesError("Failed to save active nodes.");
    } finally {
      setActiveNodesStatus("");
    }
  };

  const handleRemoveActiveNode = async (nodeId) => {
    const filtered = activeNodes.filter((id) => id !== nodeId);
    try {
      setActiveNodesStatus("Saving...");
      setActiveNodesError("");
      await setDoc(
        generalDetailsRef,
        { active_nodes: filtered },
        { merge: true }
      );
      setActiveNodes(filtered);
      setActiveNodesStatus("Saved.");
    } catch (err) {
      console.error("Failed to remove active node:", err);
      setActiveNodesError("Failed to remove active node.");
    } finally {
      setActiveNodesStatus("");
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Podcast landing copy ({langCode})</div>
        <p style={{ margin: "6px 0 10px", color: "#4b5563" }}>Leave blank to use the localized default.</p>
        <div style={{ display: "grid", gap: 8 }}>
          <input
            value={podcastTitle}
            onChange={(event) => setPodcastTitle(event.target.value)}
            placeholder="Title"
            style={{ padding: "9px 11px", border: "1px solid #d1d5db", borderRadius: 8 }}
          />
          <input
            value={podcastCta}
            onChange={(event) => setPodcastCta(event.target.value)}
            placeholder="Button label"
            style={{ padding: "9px 11px", border: "1px solid #d1d5db", borderRadius: 8 }}
          />
          <button type="button" onClick={savePodcastCopy} style={{ padding: "9px 14px" }}>Save</button>
        </div>
        {podcastCopyStatus && <div style={{ marginTop: 7, color: "#64748b", fontSize: 13 }}>{podcastCopyStatus}</div>}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Location mode</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Single-location podcasts skip address entry and use the first active node for the selected language.
        </p>
        <div
          role="radiogroup"
          aria-label="Podcast location mode"
          style={{
            display: "inline-grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 4,
            width: "min(100%, 420px)",
            padding: 4,
            borderRadius: 10,
            background: "#f1f5f9"
          }}
        >
          {[
            { value: "single", label: "Single location" },
            { value: "multiple", label: "Multiple locations" }
          ].map((option) => {
            const selected = locationMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={locationModeLoading || locationModeSaving}
                onClick={() => saveLocationMode(option.value)}
                style={{
                  padding: "9px 12px",
                  border: selected ? "1px solid #2563eb" : "1px solid transparent",
                  borderRadius: 8,
                  background: selected ? "#fff" : "transparent",
                  color: selected ? "#1d4ed8" : "#475569",
                  boxShadow: selected ? "0 1px 3px rgba(15, 23, 42, 0.1)" : "none",
                  fontWeight: 700,
                  cursor: locationModeLoading || locationModeSaving ? "wait" : "pointer"
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {(locationModeLoading || locationModeSaving || locationModeError) && (
          <div style={{ color: locationModeError ? "#b91c1c" : "#64748b", fontSize: 13, fontWeight: 600 }}>
            {locationModeError || (locationModeSaving ? "Saving..." : "Loading...")}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Default simulation</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Pick which simulation loads when this podcast editor opens.
        </p>
        <div style={{ fontSize: 13, color: "#374151" }}>
          Saved default:{" "}
          <strong>{defaultSimulationId ? simulationNameForId(defaultSimulationId) : "Not set"}</strong>
        </div>
        <SimulationSelector
          simulations={simulations}
          selectedId={defaultSimulationDraft || defaultSimulationId}
          onSelect={onDefaultSimulationSelect}
          variant="light"
        />
        {(isSavingDefaultSimulation || defaultSimulationStatus || defaultSimulationError) && (
          <div
            style={{
              color: defaultSimulationError ? "#b91c1c" : "#065f46",
              fontWeight: 600
            }}
          >
            {isSavingDefaultSimulation
              ? "Saving..."
              : defaultSimulationError || defaultSimulationStatus || ""}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>One car per household simulation</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Pick the simulation used to calculate the one-car-per-household timing for the same generated routes.
        </p>
        <div style={{ fontSize: 13, color: "#374151" }}>
          Saved one-car simulation:{" "}
          <strong>{oneCarSimulationId ? simulationNameForId(oneCarSimulationId) : "Not set"}</strong>
        </div>
        <SimulationSelector
          simulations={simulations}
          selectedId={oneCarSimulationDraft || oneCarSimulationId}
          onSelect={onOneCarSimulationSelect}
          variant="light"
        />
        {(isSavingOneCarSimulation || oneCarSimulationStatus || oneCarSimulationError) && (
          <div
            style={{
              color: oneCarSimulationError ? "#b91c1c" : "#065f46",
              fontWeight: 600
            }}
          >
            {isSavingOneCarSimulation
              ? "Saving..."
              : oneCarSimulationError || oneCarSimulationStatus || ""}
          </div>
        )}
      </div>

      <ImageVariablesSection
        isActive={isActive}
        podcastId={podcastId}
        onImageVariablesChange={onImageVariablesChange}
      />

      <VideoVariablesSection
        isActive={isActive}
        podcastId={podcastId}
      />

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Partner logos</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Upload partner logos to show on the public location entry screen.
        </p>

        <div style={{ display: "grid", gap: 8 }}>
          {partnerLogoTiers.map((tier) => (
            <label
              key={tier.key}
              style={{
                display: "grid",
                gridTemplateColumns: "160px minmax(180px, 1fr)",
                gap: 10,
                alignItems: "center",
                color: "#374151",
                fontSize: 13,
                fontWeight: 700
              }}
            >
              <span>{tier.key}</span>
              <input
                value={tier.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setPartnerLogoTiers((prev) =>
                    prev.map((item) => item.key === tier.key ? { ...item, name: value } : item)
                  );
                }}
                onBlur={(e) => updatePartnerLogoTierName(tier.key, e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600
                }}
              />
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={partnerLogoTier}
            onChange={(e) => setPartnerLogoTier(e.target.value)}
            disabled={partnerLogoUploading}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#374151",
              fontWeight: 600
            }}
          >
            {partnerLogoTiers.map((tier) => (
              <option key={tier.key} value={tier.key}>{tier.key}</option>
            ))}
          </select>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #2563eb",
              background: "#dbeafe",
              color: "#1d4ed8",
              fontWeight: 700,
              cursor: partnerLogoUploading ? "default" : "pointer",
              opacity: partnerLogoUploading ? 0.65 : 1
            }}
          >
            {partnerLogoUploading ? "Uploading..." : "Upload logo"}
            <input
              type="file"
              accept="image/*"
              disabled={partnerLogoUploading}
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (file) uploadPartnerLogo(file);
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
          </label>
          <button
            onClick={loadPartnerLogos}
            disabled={partnerLogosLoading || !podcastDocRef}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f3f4f6",
              color: "#374151",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {partnerLogosLoading ? "Loading..." : "Reload"}
          </button>
        </div>

        {partnerLogos.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: 12
            }}
          >
            {partnerLogos.map((logo, idx) => (
              <div
                key={`${logo?.storagePath || logo?.url || logo?.name || "logo"}-${idx}`}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  background: "#f9fafb",
                  display: "grid",
                  gap: 8
                }}
              >
                {logo?.url ? (
                  <img
                    src={logo.url}
                    alt={logo?.name || "Partner logo"}
                    style={{
                      width: "100%",
                      height: 72,
                      objectFit: "contain",
                      background: "#fff",
                      borderRadius: 8
                    }}
                  />
                ) : (
                  <div style={{ height: 72, display: "grid", placeItems: "center", color: "#6b7280" }}>No preview</div>
                )}
                <div
                  title={logo?.name || ""}
                  style={{
                    fontSize: 12,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {logo?.name || "Partner logo"}
                </div>
                <select
                  value={normalizePartnerLogoTierKey(logo)}
                  onChange={(e) => updatePartnerLogoTier(idx, e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#374151",
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  {partnerLogoTiers.map((tier) => (
                    <option key={tier.key} value={tier.key}>{tier.key}</option>
                  ))}
                </select>
                <button
                  onClick={() => removePartnerLogo(idx)}
                  style={{
                    border: "1px solid #fecaca",
                    background: "#fee2e2",
                    color: "#991b1b",
                    borderRadius: 8,
                    padding: "6px 8px",
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#6b7280" }}>No partner logos uploaded yet.</div>
        )}

        {(partnerLogoStatus || partnerLogoError) && (
          <div style={{ color: partnerLogoError ? "#b91c1c" : "#065f46", fontWeight: 700 }}>
            {partnerLogoError || partnerLogoStatus}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Default location</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Click on the map to pick a spot, then store it as the default landing view for this podcast.
        </p>
        <div style={{ fontSize: 13, color: "#374151" }}>
          Saved default:{" "}
          <strong>{defaultLocation ? formatCenter(defaultLocation) : "Not set"}</strong>
        </div>
        <div style={{ fontSize: 13, color: "#374151" }}>
          Saved zoom: <strong>{formatZoom(defaultZoom)}</strong>
        </div>
        {isPickingDefaultLocation && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 13, color: "#374151" }}>
              Pending selection:{" "}
              <strong>{formatCenter(defaultLocationDraft) || "—"}</strong>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label style={{ display: "grid", gap: 4, fontSize: 13, color: "#374151" }}>
                <span style={{ fontWeight: 700 }}>Desktop zoom</span>
                <input
                  type="number"
                  min="1"
                  max="22"
                  step="0.5"
                  value={defaultZoomDraft?.desktop ?? ""}
                  onChange={(e) => updateZoomField("desktop", e.target.value)}
                  style={{
                    width: 120,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db"
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: 4, fontSize: 13, color: "#374151" }}>
                <span style={{ fontWeight: 700 }}>Mobile zoom</span>
                <input
                  type="number"
                  min="1"
                  max="22"
                  step="0.5"
                  value={defaultZoomDraft?.mobile ?? ""}
                  onChange={(e) => updateZoomField("mobile", e.target.value)}
                  style={{
                    width: 120,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db"
                  }}
                />
              </label>
            </div>
          </div>
        )}

        {!isPickingDefaultLocation ? (
          <button
            onClick={onStartDefaultLocationPick}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #2563eb",
              background: "#dbeafe",
              color: "#1d4ed8",
              fontWeight: 600,
              cursor: "pointer",
              alignSelf: "flex-start"
            }}
          >
            Edit default view
          </button>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={onSaveDefaultLocation}
              disabled={!defaultLocationDraft || isSavingDefaultLocation}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "none",
                background: defaultLocationDraft ? "#16a34a" : "#9ca3af",
                color: "#fff",
                fontWeight: 600,
                cursor: defaultLocationDraft ? "pointer" : "not-allowed"
              }}
            >
              {isSavingDefaultLocation ? "Saving…" : "Save"}
            </button>
            <button
              onClick={onCancelDefaultLocationPick}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                background: "#f3f4f6",
                color: "#374151",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Cancel
            </button>
          </div>
        )}
        {(defaultLocationLoading || defaultLocationStatus || defaultLocationError) && (
          <div
            style={{
              color: defaultLocationError ? "#b91c1c" : "#065f46",
              fontWeight: 600
            }}
          >
            {defaultLocationLoading
              ? "Loading..."
              : defaultLocationError || defaultLocationStatus || ""}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Active nodes ({langCode})</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Add node IDs separated by commas to mark them active in this language.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {activeNodes.length === 0 ? (
            <span style={{ color: "#6b7280" }}>No active nodes yet.</span>
          ) : (
            activeNodes.map((nodeId) => (
              <span
                key={nodeId}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#eef2ff",
                  color: "#1d4ed8",
                  borderRadius: 999,
                  padding: "4px 10px",
                  fontSize: 13,
                  fontWeight: 600
                }}
              >
                {nodeId}
                <button
                  onClick={() => handleRemoveActiveNode(nodeId)}
                  style={{
                    marginLeft: 8,
                    border: "none",
                    background: "transparent",
                    color: "#1d4ed8",
                    cursor: "pointer",
                    fontSize: 14,
                    lineHeight: 1
                  }}
                  aria-label={`Remove node ${nodeId}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={activeNodesInput}
            onChange={(e) => setActiveNodesInput(e.target.value)}
            placeholder="e.g., 102, 207"
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 14
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddActiveNodes();
              }
            }}
          />
          <button
            onClick={handleAddActiveNodes}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              background: "#2563eb",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Add
          </button>
        </div>
        {(activeNodesStatus || activeNodesError) && (
          <div
            style={{
              color: activeNodesError ? "#b91c1c" : "#065f46",
              fontWeight: 600
            }}
          >
            {activeNodesError || activeNodesStatus}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Polygons (general tab)</div>
        <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
          Click <strong>Start selection</strong> and then click points on the map to trace a polygon.
          You can undo the last point, cancel, or save it to Firestore + Storage. Use the polygon key
          below when referencing <code>polygon_name</code> entries in <code>ROUTE_SCENES</code>.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={handleTogglePolygonSelection}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid",
              borderColor: isSelectingPolygon ? "#991b1b" : "#2563eb",
              background: isSelectingPolygon ? "#fee2e2" : "#dbeafe",
              color: isSelectingPolygon ? "#991b1b" : "#1d4ed8",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {isSelectingPolygon ? "Cancel selection" : "Start selection"}
          </button>
          <button
            onClick={() => onUndoPolygonPoint?.()}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              color: "#111827",
              fontWeight: 600,
              cursor: polygonPointsCount ? "pointer" : "not-allowed",
              opacity: polygonPointsCount ? 1 : 0.6
            }}
            disabled={!polygonPointsCount}
          >
            Undo point
          </button>
        </div>

        <div style={{ fontSize: 13, color: "#374151" }}>
          Points selected: <strong>{polygonPointsCount}</strong>{" "}
          {isSelectingPolygon ? "(selection active)" : ""}
        </div>

        <input
          type="text"
          value={polygonName}
          onChange={(e) => setPolygonName(e.target.value)}
          placeholder="Polygon key (letters, numbers, _ or -)"
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            fontSize: 14
          }}
        />

        <button
          onClick={handleSavePolygon}
          disabled={!canSavePolygon || polygonSaving}
          style={{
            padding: "9px 14px",
            borderRadius: 8,
            border: "none",
            background: canSavePolygon ? "#16a34a" : "#9ca3af",
            color: "#fff",
            fontWeight: 700,
            cursor: canSavePolygon ? "pointer" : "not-allowed"
          }}
        >
          {polygonSaving ? "Saving…" : "Save polygon"}
        </button>

        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            paddingTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14 }}>Upload GeoJSON polygons</div>
          <div style={{ color: "#4b5563", fontSize: 13, lineHeight: 1.4 }}>
            Upload a Polygon or MultiPolygon GeoJSON file. MultiPolygons are split into separate saved polygon keys.
          </div>
          <input
            type="text"
            value={geoJsonKeyPrefix}
            onChange={(e) => setGeoJsonKeyPrefix(e.target.value)}
            placeholder="Optional key prefix, e.g. valley_view_safe_polygon"
            disabled={geoJsonUploading}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 14
            }}
          />
          <input
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            disabled={geoJsonUploading}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              await handleGeoJsonUpload(file);
              e.target.value = "";
            }}
            style={{ fontSize: 13 }}
          />
          {geoJsonUploading && (
            <div style={{ color: "#4b5563", fontWeight: 600 }}>Uploading GeoJSON…</div>
          )}
        </div>

        {(polygonError || polygonStatus) && (
          <div
            style={{
              color: polygonError ? "#b91c1c" : "#065f46",
              fontWeight: 600
            }}
          >
            {polygonError || polygonStatus}
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Polygon list</div>
          <button
            onClick={handleRefreshPolygons}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              cursor: "pointer"
            }}
          >
            Refresh
          </button>
        </div>

        {polygonsLoading ? (
          <div style={{ color: "#4b5563" }}>Loading polygons…</div>
        ) : polygonsError ? (
          <div style={{ color: "#b91c1c", fontWeight: 600 }}>{polygonsError}</div>
        ) : !polygons.length ? (
          <div style={{ color: "#6b7280" }}>No polygons saved yet.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {polygons.map((poly) => {
              const isPreviewed = currentPreviewPolygonKey === poly.key;
              const isLoading = previewLoadingKey === poly.key;
              return (
                <li
                  key={poly.key}
                  style={{
                    border: `1px solid ${isPreviewed ? "#2563eb" : "#e5e7eb"}`,
                    background: isPreviewed ? "#eff6ff" : "#fff",
                    borderRadius: 10,
                    padding: "8px 10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{poly.displayName}</div>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    Key: <code>{poly.key}</code>
                  </div>
                  <div style={{ fontSize: 13, color: "#4b5563" }}>
                    Points: {poly.pointCount}
                    {formatCenter(poly.center) ? ` • Center ${formatCenter(poly.center)}` : ""}
                  </div>
                  <div>
                    <button
                      onClick={() => handlePreviewPolygonClick(poly)}
                      disabled={isLoading}
                      style={{
                        padding: "6px 9px",
                        borderRadius: 8,
                        border: "1px solid " + (isPreviewed ? "#1d4ed8" : "#d1d5db"),
                        background: isPreviewed ? "#1d4ed8" : "#f9fafb",
                        color: isPreviewed ? "#fff" : "#111827",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: isLoading ? "wait" : "pointer"
                      }}
                    >
                      {isLoading ? "Loading…" : isPreviewed ? "Hide preview" : "Show on map"}
                    </button>
                    <button
                      onClick={() => handleDeletePolygon(poly)}
                      style={{
                        marginLeft: 8,
                        padding: "6px 9px",
                        borderRadius: 8,
                        border: "1px solid #fecaca",
                        background: "#fef2f2",
                        color: "#991b1b",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer"
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {previewError && (
          <div style={{ marginTop: 6, color: "#b91c1c", fontWeight: 600 }}>{previewError}</div>
        )}
      </div>
    </div>
  );
};

export default GeneralDetailsTab;
